import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PostService, Post, PostComment } from '../../services/post.service';
import { AuthService } from '../../services/auth.service';
import { AvatarService } from '../../services/avatar.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-activity-feed',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './activity-feed.component.html',
  styleUrls: ['./activity-feed.component.css']
})
export class ActivityFeedComponent implements OnInit, AfterViewInit, OnDestroy {
  postForm: FormGroup;
  posts: Post[] = [];
  isLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  initialPageSize = 5; // Load fewer posts initially for faster first render
  regularPageSize = 10; // Load more posts on subsequent scrolls
  pageSize = 5; // Start with smaller size
  hasMore = true;
  error: string | null = null;
  isLoadingInitial = false;
  
  // For intersection observer
  private loadMoreTrigger: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  
  // Post creation state
  isCreatingPost = false;
  selectedMediaFiles: File[] = [];
  mediaPreviews: { file: File; url: string; type: string }[] = [];
  showMediaSelector = false;
  
  private destroy$ = new Subject<void>();
  private mediaBlobUrls: Map<string, string> = new Map(); // Cache blob URLs
  private scrollCheckTimeout: any;
  private postYouTubeCache: Map<number, { text: string; youtubeUrls: { videoId: string; embedUrl: SafeResourceUrl }[] }> = new Map();
  readonly defaultAvatarUrl = this.avatarService.getDefaultAvatarUrl();
  
  // Track loading state per post for comments
  isLoadingComments: Map<number, boolean> = new Map();

  constructor(
    private fb: FormBuilder,
    private postService: PostService,
    private authService: AuthService,
    private avatarService: AvatarService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.postForm = this.fb.group({
      textContent: ['', [Validators.maxLength(5000)]]
    });
  }

  ngOnInit(): void {
    // Load initial posts with smaller page size for faster rendering
    this.pageSize = this.initialPageSize;
    this.loadFeed(true);
    // Set up intersection observer for better scroll-based loading
    this.setupIntersectionObserver();
  }

  ngAfterViewInit(): void {
    // Setup intersection observer after view init
    setTimeout(() => this.createLoadMoreTrigger(), 100);
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.scrollCheckTimeout) {
      clearTimeout(this.scrollCheckTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up media preview URLs
    this.mediaPreviews.forEach(preview => {
      URL.revokeObjectURL(preview.url);
    });
    // Clean up blob URLs
    this.mediaBlobUrls.forEach(url => {
      URL.revokeObjectURL(url);
    });
    this.mediaBlobUrls.clear();
    // Clear YouTube cache
    this.postYouTubeCache.clear();
  }

  private setupIntersectionObserver(): void {
    // Create intersection observer for efficient scroll-based loading
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.shouldLoadMore()) {
              this.loadMore();
            }
          });
        },
        {
          rootMargin: '200px', // Start loading when trigger is 200px before viewport
          threshold: 0.1
        }
      );
    } else {
      // Fallback to scroll listener for older browsers
      this.setupScrollListener();
    }
  }

  private createLoadMoreTrigger(): void {
    // Create a sentinel element at the bottom of the feed for intersection observer
    const trigger = document.getElementById('load-more-trigger');
    if (trigger && this.intersectionObserver) {
      this.loadMoreTrigger = trigger;
      this.intersectionObserver.observe(trigger);
    }
  }

  private setupScrollListener(): void {
    // Fallback scroll listener for browsers without IntersectionObserver
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    // Fallback: Use debounced scroll checks if intersection observer is not available
    if (!this.intersectionObserver) {
      if (this.scrollCheckTimeout) {
        clearTimeout(this.scrollCheckTimeout);
      }
      
      this.scrollCheckTimeout = setTimeout(() => {
        if (this.shouldLoadMore()) {
          this.loadMore();
        }
      }, 150);
    }
  }

  private shouldLoadMore(): boolean {
    // Don't load if already loading, no more posts, or initial load in progress
    if (this.isLoadingMore || !this.hasMore || this.isLoading || this.isLoadingInitial) {
      return false;
    }

    // Calculate scroll position
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Load more when user is within 300px of the bottom
    const threshold = 300;
    const isNearBottom = scrollTop + windowHeight >= documentHeight - threshold;
    
    return isNearBottom;
  }

  // Get media URL - fetch as blob if it's an authenticated endpoint
  getMediaUrl(media: any): string {
    if (!media || !media.mediaUrl) {
      console.warn('getMediaUrl: media or mediaUrl is missing', media);
      return this.defaultAvatarUrl;
    }

    // If it's already a blob URL or data URL, return as is
    if (media.mediaUrl.startsWith('blob:') || media.mediaUrl.startsWith('data:')) {
      return media.mediaUrl;
    }

    // If it's an external URL (including Bunny.net CDN URLs)
    if (media.mediaUrl.startsWith('http://') || media.mediaUrl.startsWith('https://')) {
      // Check if it's our API endpoint (needs authentication)
      if (media.mediaUrl.includes('/api/post/media/')) {
        const cacheKey = media.mediaUrl;
        // Return cached blob URL if available
        if (this.mediaBlobUrls.has(cacheKey)) {
          return this.mediaBlobUrls.get(cacheKey)!;
        }
        
        // Fetch as blob with authentication (interceptor will add token)
        this.fetchMediaAsBlob(media.mediaUrl, cacheKey);
        // Return original URL for now - will be replaced by blob URL when loaded
        // Note: img src binding will update automatically when blob URL is available
        return media.mediaUrl;
      }
      
      // It's an external URL (like Bunny.net CDN) - return directly
      // Bunny.net CDN URLs should work directly without authentication
      console.log('getMediaUrl: Returning external CDN URL:', media.mediaUrl);
      return media.mediaUrl;
    }

    // Fallback for relative URLs or other formats
    console.warn('getMediaUrl: Unrecognized URL format:', media.mediaUrl);
    return media.mediaUrl;
  }

  private fetchMediaAsBlob(url: string, cacheKey: string): void {
    // Use HttpClient - the auth interceptor will automatically add the token
    this.http.get(url, { 
      responseType: 'blob'
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.mediaBlobUrls.set(cacheKey, blobUrl);
          
          // Update the media URL in posts to trigger change detection
          this.posts.forEach(post => {
            if (post.media) {
              post.media.forEach(m => {
                if (m.mediaUrl === url) {
                  m.mediaUrl = blobUrl; // Update to blob URL
                }
              });
            }
          });
          
          // Trigger change detection to update the view
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading media:', error);
          // Log more details for debugging
          if (error.status === 401) {
            console.error('Unauthorized - token may be missing or invalid');
          } else if (error.status === 404) {
            console.error('Media not found:', url);
          }
        }
      });
  }

  loadFeed(refresh: boolean = false): void {
    if (refresh) {
      this.currentPage = 1;
      this.posts = [];
      this.hasMore = true;
      this.isLoadingInitial = true;
      this.pageSize = this.initialPageSize; // Reset to initial size
    }

    if (!this.hasMore || (this.isLoading && !refresh) || this.isLoadingMore) return;

    this.isLoading = refresh && this.currentPage === 1;
    this.isLoadingMore = !refresh && this.currentPage > 1;
    this.error = null;

    // Use smaller page size for first load, larger for subsequent loads
    const currentPageSize = this.currentPage === 1 ? this.initialPageSize : this.regularPageSize;
    
    this.postService.getFeed(this.currentPage, currentPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const newPosts = response.data.posts || [];
            if (refresh) {
              // Sort by createdAt descending (most recent first)
              this.posts = newPosts.sort((a: Post, b: Post) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
              // Clear cache on refresh
              this.postYouTubeCache.clear();
            } else {
              // Append new posts, avoiding duplicates
              const existingIds = new Set(this.posts.map((p: Post) => p.id));
              const uniqueNewPosts = newPosts.filter((p: Post) => !existingIds.has(p.id));
              // Append and sort to maintain most recent first order
              this.posts = [...this.posts, ...uniqueNewPosts].sort((a: Post, b: Post) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
            }
            // Process YouTube links for all posts and initialize displayed comments count
            this.posts.forEach((post: Post) => {
              if (post.textContent && !this.postYouTubeCache.has(post.id)) {
                this.postYouTubeCache.set(post.id, this.getPostContentWithYouTube(post.textContent));
              }
              // Comments are already loaded from backend (5 initially)
              // No need to track displayed count separately
            });
            this.hasMore = response.data.hasMore || false;
            this.currentPage++;
            
            // After first load, switch to regular page size for subsequent loads
            if (this.currentPage === 2) {
              this.pageSize = this.regularPageSize;
            }
            
            // Update intersection observer trigger after new posts are loaded
            setTimeout(() => {
              if (this.loadMoreTrigger) {
                this.intersectionObserver?.observe(this.loadMoreTrigger);
              } else {
                this.createLoadMoreTrigger();
              }
            }, 100);
          }
          this.isLoading = false;
          this.isLoadingInitial = false;
          this.isLoadingMore = false;
        },
        error: (err) => {
          this.error = 'Failed to load posts';
          this.isLoading = false;
          this.isLoadingInitial = false;
          this.isLoadingMore = false;
          console.error('Error loading feed:', err);
        }
      });
  }

  onMediaSelected(event: any): void {
    const files: File[] = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Filter and validate files
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
    const maxImageSize = 10 * 1024 * 1024; // 10MB
    const maxVideoSize = 50 * 1024 * 1024; // 50MB

    files.forEach(file => {
      const isImage = allowedImageTypes.includes(file.type);
      const isVideo = allowedVideoTypes.includes(file.type);
      const maxSize = isVideo ? maxVideoSize : maxImageSize;

      if (!isImage && !isVideo) {
        this.error = `${file.name} is not a supported file type`;
        return;
      }

      if (file.size > maxSize) {
        this.error = `${file.name} is too large (max ${isVideo ? '50MB' : '10MB'})`;
        return;
      }

      this.selectedMediaFiles.push(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.mediaPreviews.push({
          file: file,
          url: e.target.result,
          type: isImage ? 'image' : 'video'
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    event.target.value = '';
  }

  removeMedia(index: number): void {
    const preview = this.mediaPreviews[index];
    URL.revokeObjectURL(preview.url);
    this.mediaPreviews.splice(index, 1);
    this.selectedMediaFiles.splice(index, 1);
  }

  toggleMediaSelector(): void {
    this.showMediaSelector = !this.showMediaSelector;
  }

  onCreatePost(): void {
    const textContent = this.postForm.get('textContent')?.value?.trim();
    
    // Validate that at least text or media is provided
    if (!textContent && this.selectedMediaFiles.length === 0) {
      this.error = 'Please add text or at least one media file';
      return;
    }

    this.isCreatingPost = true;
    this.error = null;

    this.postService.createPost({
      textContent: textContent || undefined,
      mediaFiles: this.selectedMediaFiles.length > 0 ? this.selectedMediaFiles : undefined
    }).subscribe({
      next: (response) => {
        if (response.success) {
          // Reset form
          this.postForm.reset();
          this.selectedMediaFiles = [];
          this.mediaPreviews.forEach(p => URL.revokeObjectURL(p.url));
          this.mediaPreviews = [];
          this.showMediaSelector = false;
          
          // Reload feed to show new post at the top
          this.loadFeed(true);
          // Scroll to top to see the new post
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
        this.isCreatingPost = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Failed to create post';
        this.isCreatingPost = false;
        console.error('Error creating post:', err);
      }
    });
  }

  onLikePost(post: Post): void {
    this.postService.toggleLike(post.id).subscribe({
      next: (response) => {
        if (response.success) {
          // Update post locally
          post.isLikedByCurrentUser = response.data.liked;
          if (response.data.liked) {
            post.likeCount++;
          } else {
            post.likeCount = Math.max(0, post.likeCount - 1);
          }
        }
      },
      error: (err) => {
        console.error('Error toggling like:', err);
      }
    });
  }

  onAddComment(post: Post, content: string): void {
    if (!content.trim()) return;

    this.postService.addComment(post.id, content.trim()).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          if (!post.comments) {
            post.comments = [];
          }
          // Add new comment to the end (newest comments go to the end)
          post.comments.push(response.data);
          post.commentCount++;
          
          // Clear comment input
          const commentInput = document.getElementById(`comment-input-${post.id}`) as HTMLInputElement;
          if (commentInput) {
            commentInput.value = '';
          }
        }
      },
      error: (err) => {
        console.error('Error adding comment:', err);
      }
    });
  }

  // Get visible comments for a post (all loaded comments are visible)
  getVisibleComments(post: Post): PostComment[] {
    // Return all comments that have been loaded - backend returns 5 initially
    return post.comments || [];
  }

  // Check if there are more comments to load
  hasMoreComments(post: Post): boolean {
    const loadedComments = post.comments ? post.comments.length : 0;
    // Show button if there are more comments in the database than we've loaded
    return post.commentCount > loadedComments;
  }

  // Load more comments for a post
  loadMoreComments(post: Post): void {
    const loadedComments = post.comments ? post.comments.length : 0;
    const isLoading = this.isLoadingComments.get(post.id) || false;
    
    if (isLoading || loadedComments >= post.commentCount) {
      return;
    }

    this.isLoadingComments.set(post.id, true);
    
    // Load next batch of comments (load 10 more at a time)
    const skip = loadedComments;
    const take = 10;
    
    this.postService.getComments(post.id, skip, take).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const newComments = response.data.comments || [];
          
          if (!post.comments) {
            post.comments = [];
          }
          
          // Append new comments to existing ones
          post.comments = [...post.comments, ...newComments];
          
          // If there are still more comments, keep the count updated
          if (response.data.hasMore) {
            // We've loaded all available comments, update count if needed
            if (post.comments.length < post.commentCount) {
              // There might be more, but we've loaded what's available
            }
          }
        }
        this.isLoadingComments.set(post.id, false);
      },
      error: (err) => {
        console.error('Error loading more comments:', err);
        this.isLoadingComments.set(post.id, false);
      }
    });
  }

  // Check if comments are currently loading for a post
  isCommentsLoading(postId: number): boolean {
    return this.isLoadingComments.get(postId) || false;
  }

  onDeletePost(postId: number): void {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    this.postService.deletePost(postId).subscribe({
      next: (response) => {
        if (response.success) {
          this.posts = this.posts.filter(p => p.id !== postId);
        }
      },
      error: (err) => {
        console.error('Error deleting post:', err);
      }
    });
  }

  onImageError(event: any): void {
    console.error('Image load error:', event);
    const img = event.target as HTMLImageElement;
    if (img && img.src) {
      console.error('Failed to load image URL:', img.src);
      
      // If it's a Bunny.net URL, log it for debugging
      if (img.src.includes('b-cdn.net')) {
        console.error('Bunny.net CDN URL failed to load. Check if the file exists and the PullZone is configured correctly.');
      }
    }
    event.target.src = this.defaultAvatarUrl;
  }

  getCurrentUser(): any {
    const user = this.authService.getCurrentUser();
    // The user object from AuthService may have profileImageUrl from the API
    return user;
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  getAuthorName(author: any): string {
    const parts = [author.firstName];
    if (author.middleName) parts.push(author.middleName);
    if (author.lastName) parts.push(author.lastName);
    return parts.join(' ');
  }

  loadMore(): void {
    // Prevent multiple simultaneous loads
    if (this.isLoadingMore || !this.hasMore || this.isLoading) {
      return;
    }
    
    // Load next page
    this.loadFeed(false);
  }

  refreshFeed(): void {
    // Manual refresh from UI
    this.loadFeed(true);
    // Scroll to top after refresh
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // YouTube URL detection and extraction
  extractYouTubeVideoId(url: string): string | null {
    if (!url) return null;
    
    // Regular expressions for different YouTube URL formats
    const patterns = [
      // Standard watch URLs: youtube.com/watch?v=VIDEO_ID
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?[^#\s]*v=([^&\n?#\s]+)/,
      // Short URLs: youtu.be/VIDEO_ID
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?\n&#\s]+)/,
      // Embed URLs: youtube.com/embed/VIDEO_ID
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?\n&#\s]+)/,
      // v/ URLs: youtube.com/v/VIDEO_ID
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([^?\n&#\s]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  // Check if text contains YouTube URLs
  hasYouTubeLinks(text: string): boolean {
    if (!text) return false;
    const youtubePattern = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)[^\s]+/gi;
    return youtubePattern.test(text);
  }

  // Extract all YouTube URLs from text
  extractYouTubeUrls(text: string): string[] {
    if (!text) return [];
    const youtubePattern = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)[^\s]+/gi;
    const matches = text.match(youtubePattern);
    return matches || [];
  }

  // Get YouTube embed URL
  getYouTubeEmbedUrl(videoId: string): SafeResourceUrl {
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  // Process text content to extract YouTube links
  getPostContentWithYouTube(text: string): { text: string; youtubeUrls: { videoId: string; embedUrl: SafeResourceUrl }[] } {
    if (!text) return { text: '', youtubeUrls: [] };

    const youtubeUrls: { videoId: string; embedUrl: SafeResourceUrl }[] = [];
    const youtubeLinks = this.extractYouTubeUrls(text);

    // Extract unique video IDs
    const videoIds = new Set<string>();
    youtubeLinks.forEach(link => {
      const videoId = this.extractYouTubeVideoId(link);
      if (videoId && !videoIds.has(videoId)) {
        videoIds.add(videoId);
        youtubeUrls.push({
          videoId,
          embedUrl: this.getYouTubeEmbedUrl(videoId)
        });
      }
    });

    // Remove YouTube links from text (they'll be shown as embeds)
    let processedText = text;
    youtubeLinks.forEach(link => {
      processedText = processedText.replace(link, '').trim();
    });

    return { text: processedText, youtubeUrls };
  }

  // Get cached YouTube content for a post
  getPostYouTubeContent(postId: number): { text: string; youtubeUrls: { videoId: string; embedUrl: SafeResourceUrl }[] } {
    const cached = this.postYouTubeCache.get(postId);
    if (cached) {
      return cached;
    }
    // Fallback: find post and process
    const post = this.posts.find(p => p.id === postId);
    if (post && post.textContent) {
      const processed = this.getPostContentWithYouTube(post.textContent);
      this.postYouTubeCache.set(postId, processed);
      return processed;
    }
    return { text: '', youtubeUrls: [] };
  }
}

